import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financialOpsService } from '../services/financial-ops.service';

// Companies
export function useCompanies(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['financial-ops', 'companies', params],
    queryFn: () => financialOpsService.listCompanies(params),
  });
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: ['financial-ops', 'company', id],
    queryFn: () => financialOpsService.getCompany(id),
    enabled: !!id,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, any>) => financialOpsService.createCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'companies'] });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, any> }) =>
      financialOpsService.updateCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'companies'] });
    },
  });
}

export function useDeactivateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financialOpsService.deactivateCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'companies'] });
    },
  });
}

// Bank Accounts
export function useBankAccounts(companyId: string, params?: Record<string, any>) {
  return useQuery({
    queryKey: ['financial-ops', 'bank-accounts', companyId, params],
    queryFn: () => financialOpsService.listBankAccounts(companyId, params),
    enabled: !!companyId,
  });
}

export function useCreateBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, any>) => financialOpsService.createBankAccount(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['financial-ops', 'bank-accounts', variables.companyId],
      });
    },
  });
}

export function useUpdateBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, any> }) =>
      financialOpsService.updateBankAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'bank-accounts'] });
    },
  });
}

export function useDeactivateBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financialOpsService.deactivateBankAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'bank-accounts'] });
    },
  });
}

// Categories
export function useCategories(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['financial-ops', 'categories', params],
    queryFn: () => financialOpsService.listCategories(params),
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, any>) => financialOpsService.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'categories'] });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, any> }) =>
      financialOpsService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'categories'] });
    },
  });
}

// Transactions
export function useTransactions(params: Record<string, any>) {
  return useQuery({
    queryKey: ['financial-ops', 'transactions', params],
    queryFn: () => financialOpsService.listTransactions(params),
    enabled: !!params.companyId,
  });
}

export function useCreateTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, any>) => financialOpsService.createTransaction(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, any> }) =>
      financialOpsService.updateTransaction(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useMarkTransactionPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paidDate }: { id: string; paidDate?: string }) =>
      financialOpsService.markTransactionPaid(id, paidDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useCashFlowSummary(companyId: string, startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['financial-ops', 'cash-flow', companyId, startDate, endDate],
    queryFn: () => financialOpsService.getCashFlowSummary(companyId, startDate, endDate),
    enabled: !!companyId && !!startDate && !!endDate,
  });
}

// Taxes
export function useTaxes(params: Record<string, any>) {
  return useQuery({
    queryKey: ['financial-ops', 'taxes', params],
    queryFn: () => financialOpsService.listTaxes(params),
    enabled: !!params.companyId,
  });
}

export function useCreateTax() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, any>) => financialOpsService.createTax(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'taxes'] });
    },
  });
}

export function useUpdateTax() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, any> }) =>
      financialOpsService.updateTax(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'taxes'] });
    },
  });
}

export function useMarkTaxPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paidDate }: { id: string; paidDate?: string }) =>
      financialOpsService.markTaxPaid(id, paidDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'taxes'] });
    },
  });
}

// Payouts
export function usePayouts(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['financial-ops', 'payouts', params],
    queryFn: () => financialOpsService.listPayouts(params),
  });
}

export function useSyncPayouts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => financialOpsService.syncPayouts(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'payouts'] });
    },
  });
}

export function useConfirmPayout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      companyId,
      bankAccountId,
      notes,
    }: {
      id: string;
      companyId: string;
      bankAccountId: string;
      notes?: string;
    }) => financialOpsService.confirmPayout(id, { companyId, bankAccountId, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'payouts'] });
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useDisputePayout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      financialOpsService.disputePayout(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'payouts'] });
    },
  });
}

export function useRevertPayout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financialOpsService.revertPayout(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'payouts'] });
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useLinkPayoutBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bankAccountId }: { id: string; bankAccountId: string }) =>
      financialOpsService.linkPayoutBankAccount(id, bankAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'payouts'] });
    },
  });
}

// Closings
export function useClosings(companyId: string, year?: number) {
  return useQuery({
    queryKey: ['financial-ops', 'closings', companyId, year],
    queryFn: () => financialOpsService.listClosings(companyId, year),
    enabled: !!companyId,
  });
}

export function useCreateOrRecalculateClosing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, month, year }: { companyId: string; month: number; year: number }) =>
      financialOpsService.createOrRecalculateClosing(companyId, month, year),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'closings'] });
    },
  });
}

export function useFinalizeClosing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financialOpsService.finalizeClosing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'closings'] });
    },
  });
}

export function useReopenClosing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financialOpsService.reopenClosing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'closings'] });
    },
  });
}

export function useExportClosing() {
  return useMutation({
    mutationFn: (id: string) => financialOpsService.exportClosing(id),
  });
}

export function useDre(closingId: string | undefined) {
  return useQuery({
    queryKey: ['financial-ops', 'dre', closingId],
    queryFn: () => financialOpsService.getDre(closingId!),
    enabled: !!closingId,
  });
}

// Accountant report
export function useAccountantReport(
  params: { productId: number; start: string; end: string },
  enabled = true
) {
  return useQuery({
    queryKey: ['financial-ops', 'accountant-report', params],
    queryFn: () => financialOpsService.getAccountantReport(params),
    enabled: enabled && !!params.productId && !!params.start && !!params.end,
  });
}

export function useClosingAccountantReport(closingId: string | undefined) {
  return useQuery({
    queryKey: ['financial-ops', 'accountant-report', closingId],
    queryFn: () => financialOpsService.getClosingAccountantReport(closingId!),
    enabled: !!closingId,
  });
}

export function useSyncClosingAccountantReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (closingId: string) => financialOpsService.syncClosingAccountantReport(closingId),
    onSuccess: (_data, closingId) => {
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'accountant-report'] });
      queryClient.invalidateQueries({
        queryKey: ['financial-ops', 'accountant-report', closingId],
      });
    },
  });
}

// Simulador da Reforma Tributária
export function useTaxReformDefaults(companyId: string) {
  return useQuery({
    queryKey: ['financial-ops', 'tax-reform-defaults', companyId],
    queryFn: () => financialOpsService.getTaxReformDefaults(companyId),
    enabled: !!companyId,
  });
}

export function useSimulateTaxReform() {
  return useMutation({
    mutationFn: (data: Record<string, any>) => financialOpsService.simulateTaxReform(data),
  });
}

// Attachments
export function useTransactionAttachments(transactionId: string) {
  return useQuery({
    queryKey: ['financial-ops', 'attachments', transactionId],
    queryFn: () => financialOpsService.listAttachments(transactionId),
    enabled: !!transactionId,
  });
}

export function useUploadAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, file }: { transactionId: string; file: File }) =>
      financialOpsService.uploadAttachment(transactionId, file),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['financial-ops', 'attachments', variables.transactionId],
      });
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}

export function useDeleteAttachment(transactionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financialOpsService.deleteAttachment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['financial-ops', 'attachments', transactionId],
      });
      queryClient.invalidateQueries({ queryKey: ['financial-ops', 'transactions'] });
    },
  });
}
